/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { DELETE_VALUE, UPDATE_VALUE, UPLOAD_FILE } from "../graphql";
import { VApplication } from "../../../application";
import { CREATE_VALUE } from "../graphql/createValue.mutation";

const is = require("is_js");
const log: any = LoggerModule?.getInstance();

export interface ValueFormData {
  name?: string;
  inventory?: string | number;
  disclaimer?: string;
  deadline?: string;
  minDownPayment?: string | number;
  topupPrice?: string | number;
  position?: string | number;
  isMandatory?: boolean;
  isVisible?: boolean;
  isLabel?: boolean;
  presentation?: string;
  section?: string;
  costumeAttribute?: string;
  [key: string]: any;
}

export interface UpdateValueProps {
  formData: ValueFormData;
  id: string;
}

export interface CreateValueProps extends ValueFormData {
  formData?: ValueFormData;
  id?: string;
}

export interface DeleteValueProps {
  id: string;
  costumeTypeId?: string;
}

const VALUE_FORM_FIELDS = [
  "name",
  "inventory",
  "disclaimer",
  "minDownPayment",
  "deadline",
  "topupPrice",
  "position",
  "isMandatory",
  "isVisible",
  "isLabel",
];

const VALUE_PARENT_FIELDS = ["presentation", "section", "costumeAttribute"];

const FLOAT_FIELDS: string[] = [];
const INT_FIELDS = ["inventory"];

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const trimIfString = (value: any) =>
  typeof value === "string" ? value.trim() : value;

const toNumberOrSkip = (value: any) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const toIntOrSkip = (value: any) => {
  const num = toNumberOrSkip(value);
  return num === undefined ? undefined : parseInt(String(num), 10);
};

const buildAllowedPayload = (args: any, includeParentFields = true) => {
  const source = {
    ...(args?.formData ?? args ?? {}),
  };

  VALUE_PARENT_FIELDS.forEach((field) => {
    if (
      includeParentFields &&
      (source[field] === undefined ||
        source[field] === null ||
        source[field] === "") &&
      args?.[field] !== undefined
    ) {
      source[field] = args[field];
    }
  });

  const allowedFields = includeParentFields
    ? [...VALUE_FORM_FIELDS, ...VALUE_PARENT_FIELDS]
    : VALUE_FORM_FIELDS;

  return allowedFields.reduce((payload: any, field) => {
    if (!Object.prototype.hasOwnProperty.call(source, field)) return payload;

    let value = source[field];

    if (FLOAT_FIELDS.includes(field)) {
      value = toNumberOrSkip(value);
      if (value === undefined) return payload;
    }

    if (INT_FIELDS.includes(field)) {
      value = toIntOrSkip(value);
      if (value === undefined) return payload;
    }

    if (VALUE_PARENT_FIELDS.includes(field)) {
      if (value !== undefined && value !== null && value !== "") {
        payload[field] = value;
      }
      return payload;
    }

    if (field === "topupPrice" || field === "minDownPayment") {
      if (value !== undefined && value !== null) value = String(value);
    }

    if (value !== undefined) {
      payload[field] = field === "name" ? trimIfString(value) : value;
    }

    return payload;
  }, {});
};

export interface IValueMethods extends IPluginMethodMap {
  updateValue(args: any): Promise<any>;
  createValue(args: any): Promise<any>;
  deleteValue(args: any): Promise<any>;
}

export class ValueService implements IAgentPlugin {
  readonly methods: IValueMethods;

  constructor() {
    this.methods = {
      updateValue: this.updateValue.bind(this),
      createValue: this.createValue.bind(this),
      deleteValue: this.deleteValue.bind(this),
    };
  }

  async createValue(args: CreateValueProps) {
    log?.blue?.("createValue server component:", args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const data = buildAllowedPayload(args, true);

        const res = await client.mutate({
          mutation: CREATE_VALUE,
          variables: { data },
          fetchPolicy: "no-cache",
        });

        return res?.data?.createCostumeValue;
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.find((item: any) => item.extensions)
        ?.extensions?.display_message;
      throw Error(msg || err?.message);
    }
  }

  async updateValue(args: UpdateValueProps) {
    log?.blue?.("updateValue server component:", args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const update = buildAllowedPayload(args, false);

        const res = await client.mutate({
          mutation: UPDATE_VALUE,
          variables: {
            filter: { _id: args?.id },
            update,
          },
          fetchPolicy: "no-cache",
        });

        return res?.data?.updateCostumeValue;
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.find((item: any) => item.extensions)
        ?.extensions?.display_message;
      throw Error(msg || err?.message);
    }
  }

  async deleteValue(args: DeleteValueProps) {
    log?.blue?.("deleteValue server component:", args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_VALUE,
          variables: {
            filter: { _id: args?.id },
            costumeTypeId: args?.costumeTypeId,
          },
          fetchPolicy: "no-cache",
        });

        return res?.data;
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.find((item: any) => item.extensions)
        ?.extensions?.display_message;
      throw Error(msg || err?.message);
    }
  }

  async uploadFile() {
    return [];
  }

  private isValidHttpUrl(str: string) {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(str);
  }

  private getFileTypeFromBase64(base64String: string) {
    const matches = base64String.match(/^data:image\/(\w+)/);
    return matches && matches.length === 2 ? matches[1] : null;
  }
}
